import Modal from "@/components/atoms/Modal";
import CsmAvailability from "@/components/molecules/calendar/CsmAvailability";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import StoreNoteModal from "@/components/molecules/modals/StoreNoteModal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import SoftBadge from "@/components/atoms/SoftBadge";
import { canManageMontageMeeting } from "@/helpers/MeetingHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import User from "@/interfaces/IUser";
import { NoteForm } from "@/interfaces/Note";
import { useUpdateCompanyMeetingMutation } from "@/services/companies/meetings";
import {
  useDestroyMeetingMutation,
  useUpdateMeetingMutation,
} from "@/services/meetings";
import { useGetMontageMeetingStatusesQuery } from "@/services/montageMeetingStatuses";
import {
  useAssignMontageMeetingStatusMutation,
  useDeleteMontageMeetingNoteMutation,
  useGetMontageMeetingNotesQuery,
  useReassignMontageMeetingOwnerMutation,
  useStoreMontageMeetingNoteMutation,
} from "@/services/montageMeetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faChevronDown,
  faChevronRight,
  faCircleInfo,
  faClock,
  faMessage,
  faNoteSticky,
  faRightLeft,
  faTag,
  faTrash,
  faUpRightFromSquare,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SingleValue } from "react-select";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

/** Jour, heure et CSM doivent faire la même hauteur pour s'aligner. */
const FIELD_HEIGHT = 38;

interface StatusOption {
  value: string;
  label: string;
  backgroundColor: string;
  textColor: string;
}

export interface MontageMeetingModalData {
  id: string;
  companyId: string | null;
  /** Nom de l'entreprise, ou titre d'un montage sans entreprise. */
  companyName: string;
  startAt: string;
  durationMinutes?: number;
  authorId: string | null;
  authorName: string | null;
  csmOwnerId: string | null;
  csmOwnerName: string | null;
  montageStatusId: string | null;
  montageStatusName: string | null;
  /** Commercial et télépro de la fiche entreprise, déjà présents dans le payload. */
  aeName: string | null;
  sdrName: string | null;
  /** Calculé par l'API : la modale ne connaît pas les liaisons commerciales. */
  canManage: boolean;
}

const MontageMeetingModal: ForwardRefRenderFunction<{
  showOrHiddenModal: (data?: MontageMeetingModalData) => void;
}> = (_, ref) => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const NoteModalRef = useRef<ElementRef<typeof StoreNoteModal>>(null);
  const UpdateModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [meeting, setMeeting] = useState<MontageMeetingModalData | null>(null);
  const [selectedCsm, setSelectedCsm] = useState<User | null>(null);
  // La grille et la saisie libre ADMIN/CSM partagent la même heure sélectionnée.
  const [newDate, setNewDate] = useState("");
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const { loadUsersOptions } = useLoadOptions();
  const [reassignAsync, { isLoading }] =
    useReassignMontageMeetingOwnerMutation();
  const [updateStartAtAsync, { isLoading: isUpdatingStartAt }] =
    useUpdateCompanyMeetingMutation();
  const [updateMeetingAsync, { isLoading: isUpdatingMeeting }] =
    useUpdateMeetingMutation();
  const { data: notes } = useGetMontageMeetingNotesQuery(meeting?.id ?? "", {
    skip: !meeting,
  });
  const [storeNoteAsync, { isLoading: isStoringNote }] =
    useStoreMontageMeetingNoteMutation();
  const [deleteNoteAsync] = useDeleteMontageMeetingNoteMutation();
  const [destroyMeetingAsync] = useDestroyMeetingMutation();
  const { data: statuses } = useGetMontageMeetingStatusesQuery(
    { pageIndex: 0, pageSize: 100 },
    { skip: !meeting },
  );
  const [assignStatusAsync, { isLoading: isAssigningStatus }] =
    useAssignMontageMeetingStatusMutation();
  // Le droit vient de l'API : elle seule sait si l'utilisateur est le commercial
  // rattaché à l'entreprise. Repli sur l'ancien calcul le temps qu'une API plus
  // ancienne puisse encore être en ligne — les deux se déploient séparément.
  const canManage =
    !!meeting &&
    (meeting.canManage ?? canManageMontageMeeting(currentUser, meeting));
  const canReassign = checkIfUserHasRoles(currentUser, ["ADMIN", "CSM"]);
  // ADMIN et CSM posent où ils veulent, créneau déjà pris compris — même règle
  // que l'API (MontageSlotService). Un commercial reste tenu par les créneaux
  // libres et les heures pleines.
  const canOverbook = canReassign;
  // Le rendez-vous a toujours un CSM : on part du sien, qu'on peut remplacer
  // mais qu'on n'a pas à redemander. Un commercial, lui, ne le choisit pas du
  // tout — sans ce repli il ne verrait aucune grille.
  const availabilityCsmId = selectedCsm?.id ?? meeting?.csmOwnerId ?? null;

  // Option d'affichage du CSM courant. `selectedCsm` reste nul tant qu'on n'a
  // rien changé, ce qui garde `csmChanged` faux : pré-remplir n'est pas choisir.
  const currentCsmOption =
    meeting?.csmOwnerId && meeting.csmOwnerName
      ? ({
          id: meeting.csmOwnerId,
          firstname: meeting.csmOwnerName,
          lastname: "",
        } as User)
      : null;

  const statusOptions: StatusOption[] = (statuses?.data ?? []).map(
    (status) => ({
      value: status.id,
      label: status.name,
      backgroundColor: status.backgroundColor,
      textColor: status.textColor,
    }),
  );
  const selectedStatus =
    statusOptions.find((option) => option.value === meeting?.montageStatusId) ??
    null;

  const showOrHiddenModal = (data?: MontageMeetingModalData) => {
    setMeeting(data ?? null);
    setSelectedCsm(null);
    setNewDate(
      data ? DateTime.fromISO(data.startAt).toFormat("yyyy-MM-dd") : "",
    );
    setSelectedHour(null);
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  // Après une suppression réussie, rouvrir la fiche n'aurait aucun sens : le
  // rendez-vous n'existe plus. Ce drapeau distingue « j'ai renoncé » de
  // « c'est fait », les deux passant par la même fermeture.
  const deletedRef = useRef(false);

  const onOpenDeleteModal = () => {
    deletedRef.current = false;
    ModalRef.current?.showOrHiddenModal();
    DeleteModalRef.current?.showOrHiddenModal(meeting?.id);
  };

  const onDeleteModalHidden = () => {
    if (deletedRef.current) return;
    ModalRef.current?.showOrHiddenModal();
  };

  const onDeleteMeeting = (id: string) => {
    // L'effet vit dans la promesse, pas dans le rendu du toast : sans conteneur
    // de toasts monté, ce rendu n'a pas lieu et la fiche se rouvrirait sur un
    // rendez-vous supprimé.
    const deletion = destroyMeetingAsync(id)
      .unwrap()
      .then((result) => {
        deletedRef.current = true;
        DeleteModalRef.current?.showOrHiddenModal();
        return result;
      });

    toast.promise(deletion, {
      pending: "Suppression du rendez-vous en cours...",
      success: "Le rendez-vous a bien été supprimé",
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 403) {
            return "Vous n'avez pas le droit de supprimer ce rendez-vous.";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  // Un seul bouton applique les deux changements. Le CSM passe en premier : la
  // grille montre les disponibilités du CSM RETENU, donc le créneau choisi est
  // libre pour lui, pas forcément pour l'ancien propriétaire.
  const csmChanged = !!selectedCsm && selectedCsm.id !== meeting?.csmOwnerId;
  const timeChanged =
    !!selectedHour &&
    !!newDate &&
    `${newDate}T${selectedHour}` !==
      (meeting
        ? DateTime.fromISO(meeting.startAt).toFormat("yyyy-MM-dd'T'HH:mm")
        : "");
  const canSubmitUpdate = timeChanged || csmChanged;

  const onSubmitUpdate = () => {
    if (!meeting || !canSubmitUpdate) return;

    const apply = async () => {
      if (csmChanged && selectedCsm) {
        await reassignAsync([meeting.id, selectedCsm.id]).unwrap();
      }
      if (timeChanged) {
        const startAt = `${newDate}T${selectedHour}`;
        // Sans entreprise, pas de route sous /companies : PUT /meetings/:id
        // applique la même règle de créneau (MeetingsController.update).
        return meeting.companyId
          ? updateStartAtAsync([
              meeting.companyId,
              meeting.id,
              { startAt },
            ]).unwrap()
          : updateMeetingAsync([meeting.id, { startAt }]).unwrap();
      }
      return undefined;
    };

    toast.promise(apply(), {
      pending: "Modification du rendez-vous en cours...",
      success: {
        render({ data }) {
          setMeeting((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...(data ? { startAt: data.startAt } : {}),
              ...(csmChanged && selectedCsm
                ? {
                    csmOwnerId: selectedCsm.id,
                    csmOwnerName: `${selectedCsm.firstname} ${selectedCsm.lastname}`,
                  }
                : {}),
            };
          });
          setSelectedCsm(null);
          setSelectedHour(null);
          UpdateModalRef.current?.showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              Le rendez-vous a bien été modifié 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          const message = (data?.data as { message?: string } | undefined)
            ?.message;
          if (message) return message;
          if (data?.status === 400) {
            return "Ce créneau n'est pas disponible pour ce CSM.";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onAssignStatus = (montageStatusId: string) => {
    if (!meeting || montageStatusId === meeting.montageStatusId) return;
    const chosen = statuses?.data.find(
      (status) => status.id === montageStatusId,
    );
    toast.promise(
      assignStatusAsync({ meetingId: meeting.id, montageStatusId }).unwrap(),
      {
        pending: "Changement de statut...",
        success: {
          render() {
            setMeeting((previous) =>
              previous
                ? {
                    ...previous,
                    montageStatusId,
                    montageStatusName: chosen?.name ?? null,
                  }
                : previous,
            );
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le statut a bien été changé 🤩
              </p>
            );
          },
        },
        error: "Une erreur est survenue",
      },
    );
  };

  // Deux modales ne se superposent jamais : on referme la fiche avant d'ouvrir
  // la note, et `onHide` de la note la rouvre — que la note ait été enregistrée
  // ou simplement abandonnée, puisque enregistrer referme aussi la modale.
  const onOpenUpdateModal = () => {
    if (!meeting) return;
    const startAt = DateTime.fromISO(meeting.startAt);
    setNewDate(startAt.toFormat("yyyy-MM-dd"));
    setSelectedHour(canOverbook ? startAt.toFormat("HH:mm") : null);
    setSelectedCsm(null);
    ModalRef.current?.showOrHiddenModal();
    UpdateModalRef.current?.showOrHiddenModal();
  };

  const onUpdateModalHidden = () => {
    ModalRef.current?.showOrHiddenModal();
  };

  const onOpenNoteModal = () => {
    ModalRef.current?.showOrHiddenModal();
    NoteModalRef.current?.showOrHiddenModal();
  };

  const onNoteModalHidden = () => {
    ModalRef.current?.showOrHiddenModal();
  };

  const onAddNote = ({ message }: NoteForm) => {
    if (!meeting || !message.trim()) return;
    toast.promise(
      storeNoteAsync({
        meetingId: meeting.id,
        message: message.trim(),
      }).unwrap(),
      {
        pending: "Ajout de la note...",
        success: {
          render() {
            NoteModalRef.current?.showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La note a bien été ajoutée 🤩
              </p>
            );
          },
        },
        error: "Une erreur est survenue",
      },
    );
  };

  const onDeleteNote = (id: string) => {
    if (!meeting) return;
    deleteNoteAsync({ meetingId: meeting.id, id });
  };

  return (
    <>
      <Modal
        ref={UpdateModalRef}
        title="Modifier le rendez-vous"
        onHide={onUpdateModalHidden}
        beforeFooter={
          meeting ? (
            <Button
              variant="primary"
              disabled={
                !canSubmitUpdate ||
                isUpdatingStartAt ||
                isUpdatingMeeting ||
                isLoading
              }
              onClick={onSubmitUpdate}
            >
              Valider
            </Button>
          ) : undefined
        }
      >
        {meeting && (
          <div className="d-flex flex-column gap-3">
            {/* Jour, heure et CSM réunis dans le même formulaire. */}
            <Row className="g-3">
              {canManage && (
                <Col xs={12} sm={4}>
                  <Form.Label className="fs--1 fw-semi-bold text-700 mb-1">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Jour
                  </Form.Label>
                  <Form.Control
                    type="date"
                    aria-label="Jour du rendez-vous"
                    // Hauteur forcée des deux côtés : react-select et form-control
                    // ne se calent pas sur la même, et le thème s'en mêle.
                    style={{ height: FIELD_HEIGHT }}
                    value={newDate}
                    onChange={(e) => {
                      setNewDate(e.target.value);
                      // Seuls les commerciaux doivent rechoisir un créneau libre.
                      if (!canOverbook) setSelectedHour(null);
                    }}
                  />
                </Col>
              )}
              {canManage && canOverbook && (
                <Col xs={12} sm={3}>
                  <Form.Label
                    htmlFor="montage-meeting-time"
                    className="fs--1 fw-semi-bold text-700 mb-1"
                  >
                    Heure
                  </Form.Label>
                  <Form.Control
                    id="montage-meeting-time"
                    type="time"
                    step={60}
                    aria-label="Heure du rendez-vous"
                    style={{ height: FIELD_HEIGHT }}
                    value={selectedHour ?? ""}
                    onChange={(e) => setSelectedHour(e.target.value || null)}
                  />
                </Col>
              )}
              {canReassign && (
                <Col xs={12} sm={5}>
                  <Form.Label className="fs--1 fw-semi-bold text-700 mb-1">
                    <FontAwesomeIcon icon={faRightLeft} className="me-2" />
                    CSM
                  </Form.Label>
                  <SelectPaginateAsync
                    loadOptions={loadUsersOptions}
                    styles={{
                      control: (base: Record<string, unknown>) => ({
                        ...base,
                        height: FIELD_HEIGHT,
                        minHeight: FIELD_HEIGHT,
                      }),
                    }}
                    additional={{ pageIndex: 0, roles: ["CSM"] }}
                    getOptionLabel={(option: User) =>
                      `${option.firstname} ${option.lastname}`.trim()
                    }
                    getOptionValue={(option: User) => option.id.toString()}
                    value={selectedCsm ?? currentCsmOption}
                    onChange={(value) => {
                      setSelectedCsm(value as SingleValue<User>);
                    }}
                    placeholder="Sélectionner un CSM..."
                  />
                </Col>
              )}
            </Row>

            {/* Une grille cliquable ne s'annonce pas d'elle-même : le texte dit
              l'action, puis confirme ce qui est retenu. */}
            {canManage && (
              <p className="fs--1 mb-0 text-700">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="me-2 text-info"
                />
                {!availabilityCsmId
                  ? "Sélectionnez un CSM pour voir ses créneaux disponibles."
                  : selectedHour
                    ? `Créneau retenu : ${selectedHour}. ${
                        canOverbook
                          ? "Modifiez l'heure ou cliquez sur un autre créneau pour le changer."
                          : "Cliquez sur un autre créneau pour le changer."
                      }`
                    : canOverbook
                      ? "Saisissez l'heure de votre choix ou cliquez sur un créneau. Vous pouvez choisir une heure hors horaires ou déjà prise."
                      : "Cliquez sur un créneau vert pour choisir l'heure du rendez-vous."}
              </p>
            )}

            {availabilityCsmId && (
              <CsmAvailability
                date={newDate}
                csmId={availabilityCsmId}
                onSelect={canManage ? setSelectedHour : undefined}
                selectedHour={selectedHour}
                durationMinutes={meeting?.durationMinutes ?? 60}
                excludeMeetingId={meeting?.id}
                onInvalidSelection={() => setSelectedHour(null)}
                allowBusy={canOverbook}
                allowUnavailable={canOverbook}
              />
            )}
          </div>
        )}
      </Modal>
      <StoreNoteModal
        ref={NoteModalRef}
        onSubmit={onAddNote}
        onHide={onNoteModalHidden}
      />
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDeleteMeeting}
        onHidden={onDeleteModalHidden}
        title="Souhaitez-vous vraiment supprimer ce rendez-vous ?"
      />
      <Modal
        ref={ModalRef}
        title={
          meeting ? (
            <>
              <span>{meeting.companyName}</span>
              <span className="fs--1 fw-normal text-600 mt-1">
                {`${DateTime.fromISO(meeting.startAt)
                  .setLocale("fr")
                  .toFormat("dd LLL yyyy à HH:mm")} · ${
                  meeting.authorName
                    ? `créé par ${meeting.authorName}`
                    : "auteur inconnu"
                }`}
              </span>
            </>
          ) : (
            "Rendez-vous montage"
          )
        }
        beforeFooter={
          meeting ? (
            <>
              {/* Les actions sur le rendez-vous se regroupent à gauche, séparées
                des actions de navigation par me-auto. canManage vient de l'API
                et porte seul le cas du commercial rattaché à la fiche ; le nom
                accessible et l'infobulle portent l'intitulé de chaque icône. */}
              <div className="d-flex align-items-center gap-2 me-auto">
                {canManage && (
                  <Button
                    variant="outline-danger"
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                    aria-label="Supprimer le rendez-vous"
                    title="Supprimer le rendez-vous"
                    onClick={onOpenDeleteModal}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
                {(canManage || canReassign) && (
                  <Button
                    variant="outline-primary"
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                    aria-label="Modifier le rendez-vous"
                    title="Modifier le rendez-vous"
                    onClick={onOpenUpdateModal}
                  >
                    <FontAwesomeIcon icon={faClock} />
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant="outline-info"
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44 }}
                    aria-label="Ajouter une note"
                    title="Ajouter une note"
                    onClick={onOpenNoteModal}
                  >
                    <FontAwesomeIcon icon={faMessage} />
                  </Button>
                )}
              </div>
              {meeting.companyId && (
                <Button
                  variant="primary"
                  onClick={() => navigate(`/companies/${meeting.companyId}`)}
                >
                  <FontAwesomeIcon
                    icon={faUpRightFromSquare}
                    className="me-1"
                  />
                  Ouvrir la fiche
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {meeting && (
          <>
            {/* Trois colonnes plutôt que trois lignes : sur une modale large, un
              libellé à gauche et sa valeur à droite laissaient un vide énorme
              au milieu. Les valeurs restent collées à leur libellé. */}
            <Row className="g-3 mb-3 fs--1">
              {[
                {
                  label: "Commercial",
                  value: meeting.aeName,
                  fallback: "Non assigné",
                },
                {
                  label: "Télépro",
                  value: meeting.sdrName,
                  fallback: "Non assigné",
                },
                {
                  label: "CSM",
                  value: meeting.csmOwnerName,
                  fallback: "Non assigné",
                },
              ]
                // Sans fiche, seul le CSM a un sens.
                .filter(({ label }) => meeting.companyId || label === "CSM")
                .map(({ label, value, fallback }) => (
                  <Col key={label} xs={12} sm={4}>
                    <p className="text-600 mb-0">{label}</p>
                    {value ? (
                      <p className="fw-semi-bold text-800 mb-0">{value}</p>
                    ) : (
                      <SoftBadge bg="secondary">{fallback}</SoftBadge>
                    )}
                  </Col>
                ))}
            </Row>

            {/* Seule action primaire de la modale. Libellé au-dessus, et chaque
              statut porte sa couleur : c'est elle qu'on reconnaît d'abord dans
              le calendrier, le menu doit parler le même langage. */}
            <div className="mb-3" style={{ maxWidth: "22rem" }}>
              <p className="fs--1 fw-semi-bold text-700 mb-1">
                <FontAwesomeIcon icon={faTag} className="me-2" />
                Statut
              </p>
              {canManage ? (
                <Select
                  inputId="montage-status"
                  aria-label="Statut du rendez-vous"
                  options={statusOptions}
                  value={selectedStatus}
                  isDisabled={isAssigningStatus}
                  isSearchable={false}
                  isClearable={false}
                  // La modale coupe ce qui déborde : sans portail, le menu est
                  // tronqué en bas. Le portail le sort du flux, et le z-index du
                  // composant Select (1060) le place au-dessus de la modale
                  // Bootstrap (1055).
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(option) =>
                    option && onAssignStatus((option as StatusOption).value)
                  }
                  formatOptionLabel={(option) => (
                    <SoftBadge
                      bg={(option as StatusOption).backgroundColor}
                      textColor={(option as StatusOption).textColor}
                    >
                      {(option as StatusOption).label}
                    </SoftBadge>
                  )}
                />
              ) : selectedStatus ? (
                <SoftBadge
                  bg={selectedStatus.backgroundColor}
                  textColor={selectedStatus.textColor}
                >
                  {selectedStatus.label}
                </SoftBadge>
              ) : (
                <SoftBadge bg="secondary">Sans statut</SoftBadge>
              )}
            </div>

            <div className="border rounded-3 p-3 mb-3">
              <p className="fs--1 fw-semi-bold text-700 mb-2">
                <FontAwesomeIcon icon={faNoteSticky} className="me-2" />
                Notes
              </p>

              {notes?.length ? (
                <ul className="list-unstyled mb-2 d-flex flex-column gap-2">
                  {notes.map((note) => (
                    <li
                      key={note.id}
                      className="d-flex justify-content-between align-items-start gap-2 bg-100 rounded-2 p-2"
                    >
                      <span className="text-800 fs--1 text-break">
                        {note.message}
                        <span className="d-block text-600">
                          {note.author
                            ? `${note.author.firstname} ${note.author.lastname} · `
                            : ""}
                          {DateTime.fromISO(note.createdAt)
                            .setLocale("fr")
                            .toFormat("dd LLL yyyy HH:mm")}
                        </span>
                      </span>
                      {canManage && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0 flex-shrink-0"
                          aria-label="Supprimer la note"
                          onClick={() => onDeleteNote(note.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-600 fs--1 mb-2">
                  Aucune note pour l'instant.
                </p>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default forwardRef(MontageMeetingModal);
